// preis — the investment slide (v15 slide 12). The operator edits PARAMETERS only; the
// cascade (full days → full value → each sequential discount → total, whole CHF) is computed
// by the shared computePricing — the same arithmetic the server render uses, so preview and
// PPTX always print the same numbers. No free-form price prose anywhere.
//
// The «INVESTITION» eyebrow and the calculation row labels are frozen document text.
import type { PreisContent } from '../../../../shared/deckTypes.js';
import { computePricing, type PricingResult } from '../../../../shared/deckLogic.js';
import { Abs, Brand, Dkp, PageNo, Rule, chf } from './chrome.js';

const CALC_X = 510;
const CALC_W = 706;
const VAL_X = 1030;
const ROW_PITCH = 34;
const STEP_COLORS = ['#3F7B78', '#2E5F5C', '#8A6F3E'];

export default function PreisPreview({ content, pageNo }: {
  content: PreisContent;
  pageNo?: number | null;
}) {
  const c = content;
  let pricing: PricingResult | null = null;
  if (c.months && c.days_per_month && c.day_rate_chf) {
    pricing = computePricing({
      months: c.months,
      daysPerMonth: c.days_per_month,
      utilizationPct: c.utilization_pct ?? 100,
      dayRateChf: c.day_rate_chf,
      discounts: (c.discounts ?? []).map((d) => ({ label: d.label ?? '', pct: d.pct ?? 0 })),
    });
  }

  const rows: Array<{ label: string; value: string; strong?: boolean; valueColor?: string }> = [];
  if (pricing) {
    const util = c.utilization_pct !== undefined ? `  (${pricing.utilizationPct} % Auslastung)` : '';
    rows.push({
      label: `${c.months} Monate × ${c.days_per_month} Tage${util}`,
      value: `${pricing.fullDays} Tage`,
    });
    rows.push({ label: 'Standardsatz pro Tag', value: chf(c.day_rate_chf ?? 0) });
    rows.push({ label: 'Vollwert', value: chf(pricing.fullValueChf), strong: true });
    pricing.steps.forEach((s, i) => {
      rows.push({
        label: `${s.label} · −${s.pct} %`,
        value: `− ${chf(s.amountChf)}`,
        valueColor: STEP_COLORS[i % STEP_COLORS.length],
      });
    });
  }
  const totalY = 208 + rows.length * ROW_PITCH + 18;

  return (
    <Dkp>
      <Abs x={64} y={56}><span className="dkp-eyebrow">INVESTITION</span></Abs>
      {c.budget_label ? (
        <Abs x={64} y={88} w={1000}>
          <span className="dkp-mono" style={{ fontSize: 13, letterSpacing: '.04em', color: '#121316' }}>
            {c.budget_label}
          </span>
        </Abs>
      ) : null}
      <Abs x={64} y={176} w={372} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {[c.intro, c.body2, c.body3].filter((p): p is string => !!p && p.trim() !== '').map((p, i) => (
          <div key={i} style={{ fontSize: 14.5, fontWeight: 500, color: '#3A3D42', lineHeight: 1.5 }}>{p}</div>
        ))}
      </Abs>

      {/* the calculation table */}
      <Abs x={CALC_X} y={172}><span className="dkp-th" style={{ fontSize: 10 }}>KALKULATION</span></Abs>
      <Rule x={CALC_X} y={194} w={CALC_W} color="#121316" />
      {rows.map((r, i) => {
        const y = 208 + i * ROW_PITCH;
        return (
          <div key={i}>
            <Abs x={CALC_X} y={y} w={510}>
              <span style={{
                fontSize: 13, fontWeight: r.strong ? 700 : 500, color: r.strong ? '#121316' : '#3A3D42',
              }}>{r.label}</span>
            </Abs>
            <Abs x={VAL_X} y={y + 1} w={186}>
              <span className="dkp-mono" style={{
                fontSize: 12, fontWeight: r.strong ? 700 : 500,
                color: r.valueColor ?? (r.strong ? '#121316' : '#3A3D42'),
              }}>{r.value}</span>
            </Abs>
            <Rule x={CALC_X} y={y + 26} w={CALC_W} />
          </div>
        );
      })}

      {pricing ? (
        <>
          <Abs x={CALC_X} y={totalY} w={400}>
            <span className="dkp-serif" style={{ fontSize: 38 }}>{chf(pricing.totalChf)}</span>
          </Abs>
          {pricing.combinedPct > 0 ? (
            <Abs x={930} y={totalY + 16} w={286} style={{ textAlign: 'right' }}>
              <span className="dkp-mono" style={{ fontSize: 10.5, letterSpacing: '.08em', color: '#8A6F3E' }}>
                − {pricing.combinedPct} % KOMBINIERT
              </span>
            </Abs>
          ) : null}
          <Abs x={CALC_X} y={totalY + 52} w={CALC_W}>
            <span className="dkp-mono" style={{
              fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: '#6B6E75',
            }}>
              EFFEKTIV {chf(pricing.effectiveDayRateChf)} PRO TAG · EXKL. MWST.
            </span>
          </Abs>
        </>
      ) : null}

      <Rule x={64} y={648} w={1152} />
      <Brand />
      <PageNo n={pageNo} />
    </Dkp>
  );
}
